import { useEffect, useRef, useCallback, type RefObject } from "react";
import * as maplibregl from "maplibre-gl";
import { createRoot, type Root } from "react-dom/client";
import FacilityNameBubble from "@/components/FacilityNameBubble";
import { center } from "@turf/turf";
import type { FeatureCollection, Geometry } from "geojson";

export const useFacilityNameBubble = (map: RefObject<maplibregl.Map | null>) => {
  const markerRef = useRef<maplibregl.Marker | null>(null);
  const rootRef = useRef<Root | null>(null);

  // クリーンアップ関数
  const cleanup = useCallback(() => {
    // マーカーを先に削除
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }

    // Reactコンポーネントをアンマウント
    if (rootRef.current) {
      const rootToUnmount = rootRef.current;
      rootRef.current = null;
      setTimeout(() => {
        rootToUnmount.unmount();
      }, 0);
    }
  }, []);

  // 施設名吹き出しを表示
  const displayFacilityNameBubble = useCallback(
    (facilityId: string, facilityName: string, polygons: FeatureCollection<Geometry> | null) => {
      const mapInstance = map.current;
      if (!mapInstance || !polygons) return;

      cleanup();

      // 該当する施設のポリゴンを探す
      const facilityFeature = polygons.features.find(
        (feature) => feature.properties?.id === facilityId,
      );

      if (!facilityFeature) return;

      // ポリゴンの中心座標を取得
      const centerPoint = center(facilityFeature);
      const [lng, lat] = centerPoint.geometry.coordinates;

      // マーカー用のDOMエレメントを作成
      const el = document.createElement("div");
      el.style.zIndex = "10";

      // Reactコンポーネントをマウント
      const root = createRoot(el);
      root.render(<FacilityNameBubble facilityName={facilityName} />);
      rootRef.current = root;

      // マーカーを作成して地図に追加
      const marker = new maplibregl.Marker({
        element: el,
        anchor: "bottom",
        offset: [0, 20],
      })
        .setLngLat([lng, lat])
        .addTo(mapInstance);

      markerRef.current = marker;
    },
    [map, cleanup],
  );

  // 施設名吹き出しをクリア
  const clearFacilityNameBubble = useCallback(() => {
    cleanup();
  }, [cleanup]);

  // コンポーネントのアンマウント時にクリーンアップ
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    displayFacilityNameBubble,
    clearFacilityNameBubble,
  };
};
